import { AxiosInstance } from 'axios';
import gql from 'graphql-tag';

import GraphQLClient from '../../adapter/GraphQLClient';
import {
  PerformanceReportDetailRequest,
  PerformanceReportDimension,
  PerformanceReportImprovementRequest,
  PerformanceReportPeriodPattern,
  PerformanceReportSummaryRequest,
} from '../../generated/graphql';
import {
  DailySummaryPerformanceWithAllFieldsFragment,
  PeriodicDetailPerformanceWithAllFieldsFragment,
  PeriodicImprovementPerformanceWithAllFieldsFragment,
} from './../../generated/graphql';

const dailySummaryPerformanceWithAllFieldsFragment = gql`
  fragment DailySummaryPerformanceWithAllFields on DailySummaryPerformance {
    period {
      from
      to
    }
    metric
    basis {
      values {
        date
        actualValue {
          value
          type
          currency
        }
      }
    }
    creatives {
      creativeId
      values {
        date
        actualValue {
          value
          type
          currency
        }
      }
      adName
      thumbnailImageUrl
      creation {
        id
        assignee {
          id
          name
          profileImageUrl
        }
        deliverables {
          id
          createdAt
          creative {
            id
            file {
              id
              originalFileName
              url
              thumbnailUrl
              mimeType
              size
            }
          }
          intent
          state
          stateUpdatedAt
        }
      }
    }
  }
`;

const periodicDetailPerformanceWithAllFieldsFragment = gql`
  fragment PeriodicDetailPerformanceWithAllFields on PeriodicDetailPerformance {
    period {
      from
      to
    }
    creatives {
      creativeId
      adName
      thumbnailImageUrl
      creation {
        id
        assignee {
          id
          name
          profileImageUrl
        }
        deliverables {
          id
          createdAt
          creative {
            id
            file {
              id
              originalFileName
              url
              thumbnailUrl
              mimeType
              size
            }
          }
          intent
          state
          stateUpdatedAt
        }
      }
      values {
        metric
        actualValue {
          value
          type
          currency
        }
      }
    }
  }
`;

const creativeForPeriodicImprovementPerformanceWithAllFieldsFragment = gql`
  fragment CreativeForPeriodicImprovementPerformanceWithAllFields on ImprovementCreative {
    creativeId
    adName
    thumbnailImageUrl
    values {
      all {
        metric
        target
        targetValue {
          value
          type
          currency
        }
        actualValue {
          value
          type
          currency
        }
      }
      staticImage {
        metric
        target
        targetValue {
          value
          type
          currency
        }
        actualValue {
          value
          type
          currency
        }
      }
      video {
        metric
        target
        targetValue {
          value
          type
          currency
        }
        actualValue {
          value
          type
          currency
        }
      }
    }
    creation {
      id
      assignee {
        id
        name
        profileImageUrl
      }
      deliverables {
        id
        createdAt
        creative {
          id
          file {
            id
            originalFileName
            url
            thumbnailUrl
            mimeType
            size
          }
        }
        intent
        state
        stateUpdatedAt
      }
    }
  }
`;

const periodicImprovementPerformanceWithAllFieldsFragment = gql`
  ${creativeForPeriodicImprovementPerformanceWithAllFieldsFragment}
  fragment PeriodicImprovementPerformanceWithAllFields on PeriodicImprovementPerformance {
    period {
      from
      to
    }
    creatives {
      ...CreativeForPeriodicImprovementPerformanceWithAllFields
    }
  }
`;

class PerformanceReportWebApi {
  private graphqlClient: GraphQLClient;
  private httpClient: AxiosInstance;

  constructor({ graphqlClient, httpClient }: { graphqlClient: GraphQLClient; httpClient: AxiosInstance }) {
    this.graphqlClient = graphqlClient;
    this.httpClient = httpClient;
  }

  public getDailySummaryPerformance = async (
    reportId: string,
    request: PerformanceReportSummaryRequest,
  ): Promise<DailySummaryPerformanceWithAllFieldsFragment> => {
    try {
      const result = await this.graphqlClient.query(
        gql`
          ${dailySummaryPerformanceWithAllFieldsFragment}
          query getDailySummaryPerformance($reportId: ID!, $request: PerformanceReportSummaryRequest!) {
            dailySummaryPerformance(reportId: $reportId, request: $request) {
              ...DailySummaryPerformanceWithAllFields
            }
          }
        `,
        {
          reportId,
          request,
        },
      );
      return result.dailySummaryPerformance;
    } catch (err) {
      throw err;
    }
  };

  public getPeriodicDetailPerformance = async (
    reportId: string,
    request: PerformanceReportDetailRequest,
  ): Promise<PeriodicDetailPerformanceWithAllFieldsFragment> => {
    try {
      const result = await this.graphqlClient.query(
        gql`
          ${periodicDetailPerformanceWithAllFieldsFragment}
          query getPeriodicDetailPerformance($reportId: ID!, $request: PerformanceReportDetailRequest!) {
            periodicDetailPerformance(reportId: $reportId, request: $request) {
              ...PeriodicDetailPerformanceWithAllFields
            }
          }
        `,
        {
          reportId,
          request,
        },
      );
      return result.periodicDetailPerformance;
    } catch (err) {
      throw err;
    }
  };

  public getPeriodicImprovementPerformance = async (
    reportId: string,
    request: PerformanceReportImprovementRequest,
  ): Promise<PeriodicImprovementPerformanceWithAllFieldsFragment> => {
    try {
      const result = await this.graphqlClient.query(
        gql`
          ${periodicImprovementPerformanceWithAllFieldsFragment}
          query getPeriodicImprovementPerformance($reportId: ID!, $request: PerformanceReportImprovementRequest!) {
            periodicImprovementPerformance(reportId: $reportId, request: $request) {
              ...PeriodicImprovementPerformanceWithAllFields
            }
          }
        `,
        { reportId, request: { period: request.period } },
      );
      return result.periodicImprovementPerformance;
    } catch (err) {
      throw err;
    }
  };

  public postExportPerformanceReportRequest = async (
    reportId: string,
    period: PerformanceReportPeriodPattern,
    campaignId: string | null,
    dimension: PerformanceReportDimension,
  ): Promise<void> => {
    try {
      await this.httpClient.post('/export_performance_report_requests', {
        id: reportId,
        period: period,
        campaign_id: campaignId,
        dimension,
      });
    } catch (err) {
      throw err;
    }
    return;
  };
}

export default PerformanceReportWebApi;
